/// <amd-module name="SearchUI/Components/SearchPickList/SearchPickListMenu" />
import * as React from "react";
import { TextField } from "VSSUI/TextField";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { IListItemDetails, List, ListItem, IListRow } from "VSSUI/List";
import { ISearchPickListItem, ISearchPickListMenuProps } from "SearchUI/Components/SearchPickList/SearchPickList.Props";
import { SearchPickListItem } from "SearchUI/Components/SearchPickList/SearchPickListItem";
import { announce } from "VSS/Core/Util/Accessibility";
import { KeyCode } from "VSSUI/Util";
import { Intersection } from "VSSUI/Intersection";
import { FocusZoneDirection } from "VSSUI/FocusZone";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import * as Resources from "SearchUI/Search.Resources.SearchUI";

export const ALL_CHECKBOX_ITEM_ID = "2AA5457E-5E05-430C-A44A-A7C1E6B3C653";
const THRESHOLD_FILTER_COUNT_TO_RENDER_SEARCH_BOX: number = 8;

export interface ISearchPickListMenuState {
    items: ISearchPickListItem[];

    searchText: string;

    itemsDictionary: { [key: string]: ISearchPickListItem };
}

export class SearchPickListMenu extends React.Component<ISearchPickListMenuProps, ISearchPickListMenuState> {
    private list = React.createRef<List<ISearchPickListItem>>();

    private viewState: ISearchPickListMenuState;
    private searchPickListMenu: HTMLElement;

    constructor(props: ISearchPickListMenuProps) {
        super(props);
        this.state = this.viewState = this._build(props, "", true);
    }

    public render(): JSX.Element {
        const { height, width, onGetFooterMessage, isExpandable, allItemDisplayName } = this.props;
        const { items } = this.state;
        const itemsCount = items.length;
        const menuListStyle: React.CSSProperties = {};
        const itemsContainerStyle: React.CSSProperties = {};
        const menuListAriaLabel = Resources.SearchPickListMenuAriaLabel.replace("{0}", allItemDisplayName );

        if (height && width) {
            menuListStyle.width = width;
            menuListStyle.height = height;
            // reset max height
            menuListStyle.maxHeight = "none";
            itemsContainerStyle.maxHeight = "none";
        }

        return (
            <div
                id={this.props.id}
                className="search-PickListMenu"
                ref={element => (this.searchPickListMenu = element)}
                tabIndex={-1}
                style={menuListStyle}
                onBlur={this._onBlur}
                onKeyDown={this._onKeyDown}>
                {this._isSearchBoxAvailable(this.props) && (
                    <TextField
                        prefixIconProps={{ iconName: "Search" }}
                        containerClassName="search-PickListMenu--searchBox"
                        value={this.state.searchText}
                        onChange={this._onSearchTextChanged}
                        onKeyDown={this._onKeyDown}
                        placeholder={this.props.searchTextPlaceholder}
                    />
                )}
                <Intersection>
                    <div className="search-PickListMenuItems--container" 
                        role="presentation" 
                        data-is-scrollable={true} 
                        style={itemsContainerStyle}
                        aria-label={menuListAriaLabel}>
                        {!this.props.dataLoading ? (
                            <List
                                itemProvider={new ArrayItemProvider(items)}
                                ref={this.list}
                                role="listbox"
                                focuszoneProps={{
                                    circularNavigation: true,
                                    handleTabKey: false,
                                    direction: FocusZoneDirection.Vertical,
                                }}
                                renderRow={(index: number, item: ISearchPickListItem, details: IListItemDetails<ISearchPickListItem>, keys?: string) => {
                                    return (
                                        <SearchPickListItem
                                            focusableProps={details}
                                            key={item.id}
                                            item={this.state.itemsDictionary[item.id]}
                                            itemsCount={itemsCount}
                                            itemIndex={index}
                                            disabled={ignoreCaseComparer(item.id, ALL_CHECKBOX_ITEM_ID) === 0 && item.selected}
                                            onChange={this._onItemSelectionChanged}
                                        />
                                    );
                                }}
                                width="100%" />)
                            : <Spinner className="search-picklist-menu-filter-loading" label={Resources.Loading} size={SpinnerSize.small} />
                            }
                    </div>
                </Intersection>
                {
                    // Render footer section if component is provided with a delegate to get the text to display
                    !this.props.dataLoading && onGetFooterMessage && (
                        <div className="footer-message" onMouseDown={this._onHintMessageClick} aria-live="assertive">
                            {this._onGetFooterMessage(items.length, this.state.searchText)}
                        </div>
                    )}
                {isExpandable && <div className="bowtie-icon resizer bowtie-corner-resize" tabIndex={0} onMouseDown={this._onResizing} />}
            </div>
        );
    }

    /**
     * Update the list forcefully, as the items of the list may have modified even though the
     * List will appear to be the same. (shallow comparison)
     */
    public componentDidUpdate(): void {
        this.list.current && this.list.current.forceUpdate();
    }

    public componentWillReceiveProps(newProps: ISearchPickListMenuProps): void {
        // Content is updated if the number of items is modified or the items itself are modified.
        const contentUpdated: boolean =
            newProps.items.length !== this.props.items.length ||
            !this.props.items.every((item, index) => {
                const newItem = newProps.items[index];
                return item.id === newItem.id && item.resultCount === newItem.resultCount;
            });

        if (contentUpdated) {
            this.viewState = this._build(newProps, this.state.searchText, false);
            this.setState(this.viewState);
        }
    }

    /**
     * Stop mouse click event to prevent the drop-down menu from blurring.
     * @param evt
     */
    private _onHintMessageClick = (evt: React.MouseEvent<HTMLElement>): void => {
        evt.stopPropagation();
        evt.preventDefault();
    };

    private _onBlur = (evt: React.FocusEvent<HTMLElement>): void => {
        this.props.onDismiss(evt);
    };

    private _onSearchTextChanged = (evt: React.ChangeEvent<HTMLInputElement>, searchText: string): void => {
        this.viewState.searchText = searchText;

        searchText = searchText.toLowerCase().trim();

        if (searchText.length > 0) {
            this.viewState.items = this._refineItems(this.props.items || [], searchText);
        } else {
            this.viewState = this._build(this.props, "", true);
        }

        this.setState(this.viewState);
    };

    private _onItemSelectionChanged = (item: ISearchPickListItem, isChecked: boolean): void => {
        const { onSelectionChanged } = this.props;

        if (onSelectionChanged) {
            const allSelected = ignoreCaseComparer(item.id, ALL_CHECKBOX_ITEM_ID) === 0 && isChecked;
            const itemsDict = this.viewState.itemsDictionary;
            if (allSelected) {
                for (const id in itemsDict) {
                    if (itemsDict.hasOwnProperty(id)) {
                        itemsDict[id].selected = false;
                    }
                }

                itemsDict[ALL_CHECKBOX_ITEM_ID].selected = true;
                this.setState(this.viewState);

                // Invoke selection change with an empty set.
                onSelectionChanged([]);
            } else {
                itemsDict[item.id].selected = isChecked;
                const selectedItems = [];
                let allSelectedState = true;

                for (const id in itemsDict) {
                    if (ignoreCaseComparer(ALL_CHECKBOX_ITEM_ID, id) !== 0) {
                        itemsDict[id].selected && selectedItems.push(itemsDict[id]);
                        allSelectedState = allSelectedState && !itemsDict[id].selected;
                    }
                }

                itemsDict[ALL_CHECKBOX_ITEM_ID].selected = allSelectedState;
                this.setState(this.viewState);

                onSelectionChanged(selectedItems);
            }
        }
    };

    private _onKeyDown = (evt: React.KeyboardEvent<HTMLElement>): void => {
        if (evt.keyCode === KeyCode.escape) {
            this.props.onDismiss(evt);
        }
    };

    private _onResizing = (evt: React.MouseEvent<HTMLElement>): void => {
        const { onResizing } = this.props;
        if (onResizing && this.searchPickListMenu) {
            const { height, width } = this.searchPickListMenu.getBoundingClientRect();
            onResizing(width, height, evt);
        }
    };

    /**
     * Returns the state object. Given the props method populates the items in the state object.
     * If there is a valid search text items are populated after refining the list from props on searchText.
     * reorder - If set to true will set the selected items ahead in the list, and all unselected items at the back.
     * @param props
     * @param searchText
     * @param reorder
     */
    private _build(props: ISearchPickListMenuProps, searchText: string, reorder?: boolean): ISearchPickListMenuState {
        let items: ISearchPickListItem[] = props.items || [];
        const allItemSelectedState = !items.some(item => item.selected);
        const allItem: ISearchPickListItem = {
            name: props.allItemDisplayName,
            id: ALL_CHECKBOX_ITEM_ID,
            selected: allItemSelectedState,
            resultCount: 0
        };
        const checkedItems = [];
        const unCheckedItems = [];
        const itemsDictionary = {};

        items.forEach(item => {
            itemsDictionary[item.id] = item;
            item.selected && checkedItems.push(item);
            !item.selected && unCheckedItems.push(item);
        });

        itemsDictionary[ALL_CHECKBOX_ITEM_ID] = allItem;

        // If search text is non-empty and search box is available, set items after refining the list passed in props.
        if (this._isSearchBoxAvailable(props) && searchText) {
            return {
                items: this._refineItems(items, searchText),
                searchText: searchText,
                itemsDictionary: itemsDictionary
            };
        }

        if (reorder) {
            items = [allItem, ...checkedItems, ...unCheckedItems];
        } else {
            items = [allItem, ...items];
        }

        return {
            searchText: searchText,
            items: items,
            itemsDictionary: itemsDictionary
        };
    }

    private _isSearchBoxAvailable(props: ISearchPickListMenuProps): boolean {
        return props.searchable || (props.items || []).length >= THRESHOLD_FILTER_COUNT_TO_RENDER_SEARCH_BOX;
    }

    private _refineItems(items: ISearchPickListItem[], searchText: string): ISearchPickListItem[] {
        const { onRefineItems } = this.props;
        if (onRefineItems) {
            return onRefineItems(items, searchText);
        }

        return [];
    }

    private _onGetFooterMessage(itemCount: number, searchText: string): string {
        const footerMessage = this.props.onGetFooterMessage(itemCount, searchText);

        if (!this.props.disableAnnounce) {
            announce(footerMessage);
        }

        return footerMessage;
    }
}

function ignoreCaseComparer(first: string, second: string): number {
    first = first.toLowerCase();
    second = second.toLowerCase();

    if (first > second) {
        return 1;
    } else if (first < second) {
        return -1;
    }

    return 0;
}
